import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Users, Zap, Send, Wallet } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useCompany } from '@/context/CompanyContext';
import { SettingsTabs } from '@/components/settings';
import {
  IntegrationSettingsLayout,
  SettingsSection,
  ToggleRow,
  SelectRow,
  type IntegrationInfo,
} from './IntegrationSettingsLayout';

interface RivhitSettings {
  manual_push_documents:      boolean;
  rivhit_document_type:       number;
  include_payment:            boolean;
  rivhit_payment_type:        number | null;
  unmatched_client_mode:      'ask' | 'auto_create';
  rivhit_general_customer_id: number | null;
  send_email:                 boolean;
}

const DEFAULT_SETTINGS: RivhitSettings = {
  manual_push_documents:      true,
  rivhit_document_type:       1,
  include_payment:            false,
  rivhit_payment_type:        null,
  unmatched_client_mode:      'ask',
  rivhit_general_customer_id: null,
  send_email:                 false,
};

const FALLBACK_DOC_TYPES = [
  { id: 1, name: 'חשבונית מס' },
  { id: 2, name: 'חשבונית מס קבלה' },
];

interface Option { id: number; name: string }

export function RivhitIntegrationSettings() {
  const { activeCompanyId } = useCompany();
  const navigate = useNavigate();

  const [integration, setIntegration] = useState<IntegrationInfo | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [toast,       setToast]       = useState<string | null>(null);
  const [settings,    setSettings]    = useState<RivhitSettings>(DEFAULT_SETTINGS);
  const [docTypes,    setDocTypes]    = useState<Option[]>(FALLBACK_DOC_TYPES);
  const [payTypes,    setPayTypes]    = useState<Option[]>([]);
  const [optionsNote, setOptionsNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!activeCompanyId) return;
    setLoading(true); setError(null);
    try {
      const { data, error: e } = await supabase
        .from('company_integrations')
        .select('id, provider_name, credentials, settings, is_active, last_sync_at')
        .eq('company_id', activeCompanyId)
        .eq('provider_name', 'rivhit')
        .maybeSingle();
      if (e) throw e;
      if (!data) {
        navigate('/settings/integrations', { replace: true });
        return;
      }
      setIntegration(data as IntegrationInfo);
      setSettings({ ...DEFAULT_SETTINGS, ...((data.settings ?? {}) as Partial<RivhitSettings>) });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בטעינת הגדרות');
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId, navigate]);

  const loadOptions = useCallback(async () => {
    if (!activeCompanyId) return;
    const { data, error: e } = await supabase.functions.invoke('external-invoice', {
      body: { action: 'rivhit_options', provider: 'rivhit', company_id: activeCompanyId },
    });
    if (e || !data?.ok) {
      setOptionsNote(data?.error ?? 'לא הצלחנו לטעון מרווחית את רשימת סוגי המסמכים ואמצעי התשלום. מוצגת רשימה בסיסית.');
      return;
    }
    const types = Array.isArray(data.document_types) ? (data.document_types as Option[]) : [];
    const pays = Array.isArray(data.payment_types) ? (data.payment_types as Option[]) : [];
    if (types.length) setDocTypes(types);
    setPayTypes(pays);
    setOptionsNote(null);
  }, [activeCompanyId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadOptions(); }, [loadOptions]);

  const handleSave = async () => {
    if (!activeCompanyId || !integration) return;
    if (settings.include_payment && !settings.rivhit_payment_type) {
      setError('בחרו אמצעי תשלום, או כבו את רישום התשלום במסמך.');
      return;
    }
    setSaving(true); setError(null);
    try {
      const merged = { ...integration.settings, ...settings };
      const { error: e } = await supabase
        .from('company_integrations')
        .update({ settings: merged })
        .eq('id', integration.id);
      if (e) throw e;
      setIntegration(prev => prev ? { ...prev, settings: merged } : prev);
      setToast('הגדרות שמורות בהצלחה');
      setTimeout(() => setToast(null), 4000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בשמירת הגדרות');
    } finally {
      setSaving(false);
    }
  };

  const set = <K extends keyof RivhitSettings>(key: K, val: RivhitSettings[K]) =>
    setSettings(prev => ({ ...prev, [key]: val }));

  return (
    <div className="max-w-5xl mx-auto space-y-6" dir="rtl">
      <SettingsTabs />

      {toast && (
        <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
          {toast}
        </div>
      )}

      <IntegrationSettingsLayout
        providerLabel="רווחית (Rivhit)"
        logoText="🧾"
        logoBg="bg-sky-50"
        integration={integration}
        loading={loading}
        saving={saving}
        error={error}
        onSave={handleSave}
        onEditCredentials={() => navigate('/settings/integrations')}
      >
        <SettingsSection
          title="הפקת חשבונית מתעודת משלוח"
          description="כפתור בתעודת המשלוח שמפיק ברווחית מסמך עם כל שורות המוצרים"
          icon={<Send className="w-4 h-4 text-sky-600" />}
        >
          <ToggleRow
            label='הצג כפתור "הפק ברווחית"'
            description="הכפתור יופיע בתעודות משלוח למשתמשים עם הרשאת עריכה במלאי."
            checked={settings.manual_push_documents}
            onChange={v => set('manual_push_documents', v)}
          />
          <SelectRow
            label="סוג המסמך שיופק"
            description="רשימת סוגי המסמכים נטענת מחשבון הרווחית שלכם."
            value={String(settings.rivhit_document_type)}
            options={docTypes.map(t => ({ value: String(t.id), label: t.name }))}
            onChange={v => set('rivhit_document_type', Number(v))}
          />
          <ToggleRow
            label="שלח את המסמך ללקוח במייל"
            description="רווחית תשלח את המסמך לכתובת המייל השמורה בכרטיס הלקוח (אם קיימת)."
            checked={settings.send_email}
            onChange={v => set('send_email', v)}
          />
          {optionsNote && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">{optionsNote}</p>}
        </SettingsSection>

        <SettingsSection
          title="תשלום"
          description="למסמכים שכוללים קבלה (כמו חשבונית מס קבלה) יש לרשום באיזה אמצעי שולם"
          icon={<Wallet className="w-4 h-4 text-amber-600" />}
        >
          <ToggleRow
            label="רשום תשלום במסמך"
            description="הסכום הכולל של התעודה יירשם כתשלום שהתקבל."
            checked={settings.include_payment}
            onChange={v => set('include_payment', v)}
          />
          {settings.include_payment && (
            payTypes.length > 0 ? (
              <SelectRow
                label="אמצעי תשלום"
                value={settings.rivhit_payment_type ? String(settings.rivhit_payment_type) : ''}
                options={[{ value: '', label: 'בחרו אמצעי תשלום' }, ...payTypes.map(p => ({ value: String(p.id), label: p.name }))]}
                onChange={v => set('rivhit_payment_type', v ? Number(v) : null)}
              />
            ) : (
              <p className="text-xs text-gray-500">רשימת אמצעי התשלום תופיע לאחר שהחיבור לרווחית יאומת.</p>
            )
          )}
        </SettingsSection>

        <SettingsSection
          title="זיהוי לקוחות"
          description="כיצד לקוח התעודה מקושר ללקוח ברווחית. לקוח שמקושר כבר (או שיש לו ברווחית התאמה מלאה בח.פ) מזוהה אוטומטית"
          icon={<Users className="w-4 h-4 text-emerald-600" />}
        >
          <SelectRow
            label="כשהלקוח לא מזוהה ברווחית"
            description="ב&quot;שאל אותי&quot; יופיע חלון לבחירת לקוח קיים, הקמת לקוח חדש או הפקה ללקוח הכללי. כשלאותו ח.פ יש כמה סניפים, החלון יופיע תמיד."
            value={settings.unmatched_client_mode}
            options={[
              { value: 'ask',         label: 'שאל אותי' },
              { value: 'auto_create', label: 'הקם לקוח חדש אוטומטית' },
            ]}
            onChange={v => set('unmatched_client_mode', v as RivhitSettings['unmatched_client_mode'])}
          />
          <div className="flex items-start justify-between gap-4 py-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-700">מספר הלקוח הכללי ברווחית</p>
              <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">
                כרטיס &quot;לקוח מזדמן&quot; ברווחית. כשמוגדר, יופיע בחלון הבחירה כפתור &quot;לקוח כללי&quot;, וישמש גם לתעודות ללא לקוח.
              </p>
            </div>
            <input
              type="text"
              inputMode="numeric"
              dir="ltr"
              value={settings.rivhit_general_customer_id ?? ''}
              onChange={e => {
                const d = e.target.value.replace(/\D/g, '').slice(0, 9);
                set('rivhit_general_customer_id', d && Number(d) > 0 ? Number(d) : null);
              }}
              placeholder="לא מוגדר"
              className="w-32 shrink-0 text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 text-center bg-white text-gray-800
                         focus:outline-none focus:ring-2 focus:ring-sky-300"
            />
          </div>
        </SettingsSection>

        <div className="flex items-start gap-3 px-4 py-3 bg-sky-50 border border-sky-100 rounded-xl text-xs text-sky-700">
          <Zap className="w-3.5 h-3.5 shrink-0 mt-0.5 text-sky-500" />
          <p>
            <strong>שים לב:</strong> {'המע"מ נלקח מהתעודה עצמה (כולל לקוחות פטורים). כל תעודה ניתן להפיק פעם אחת בלבד, ומספר המסמך ברווחית נשמר עליה.'}
          </p>
        </div>

        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl">
          <div>
            <p className="text-sm font-medium text-gray-700">תיעוד API של רווחית</p>
            <p className="text-xs text-gray-400 mt-0.5">מדריך טכני לממשק ה-API של רווחית Online</p>
          </div>
          <a
            href="https://api.rivhit.co.il/online/RivhitOnlineAPI.svc/help"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-sky-600 border border-sky-200 bg-white rounded-lg hover:bg-sky-50 transition-colors"
          >
            <FileText className="w-3.5 h-3.5" />
            פתח תיעוד
          </a>
        </div>
      </IntegrationSettingsLayout>
    </div>
  );
}
